import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompanyQueryKey,
  useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompany,
  usePatchApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingId,
} from '@/api/generated/company-management-user/company-management-user'
import { TimeTrackingApproval } from '@/api/model'
import type { OrderType, TimesheetModel, TimesheetModelPaginatedResponse } from '@/api/model'
import { notify } from '@/lib/notify'
import { getTimeTrackingStatusLabel } from '../all-entries-components/timeTrackingLabels'
import { unwrap } from '@/utils/unwrap.ts'

const DEFAULT_PAGE_SIZE = 10

/** Active filters of the time-tracking list (employee optional, date range as ISO). */
export interface TimeTrackingEntriesFilters {
  employeeUserId?: string
  from?: string
  to?: string
  /** Only entries of this approval status (e.g. `Pending` for the approvals list). */
  approval?: TimeTrackingApproval
}

/**
 * Data layer of the time-tracking list: server-side pagination + sorting as well as
 * approving/rejecting individual entries. Table state (sort/page/size) lives here;
 * the filters come from outside (so they can be shared). Used by
 * `TimeTrackingAllEntriesPage` and `TimeTrackingEmployeeEntriesPage`.
 * Mirrors `useAbsenceEntries` of the absence module.
 */
export function useTimeTrackingEntries(serviceModuleId: number, filters: TimeTrackingEntriesFilters) {
  const { t } = useTranslation('service-modules-timetracking')
  const { employeeUserId, from, to, approval } = filters
  const queryClient = useQueryClient()

  const [sort, setSort] = useState<string>()
  const [order, setOrder] = useState<OrderType>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  // Filter change → back to the first page.
  useEffect(() => {
    setOffset(0)
  }, [employeeUserId, from, to])

  const listQuery = useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompany(
    serviceModuleId,
    {
      Offset: offset,
      Limit: limit,
      Sort: sort,
      Order: order,
      employeeUserId,
      startTimestamp: from ? dayjs(from).startOf('day').toISOString() : undefined,
      endTimestamp: to ? dayjs(to).endOf('day').toISOString() : undefined,
      approval,
    },
  )
  const list = unwrap<TimesheetModelPaginatedResponse>(listQuery.data)

  const patch = usePatchApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingId()

  async function updateStatus(entry: TimesheetModel, status: TimeTrackingApproval) {
    if (entry.approval === status) return
    setUpdatingId(entry.id)
    try {
      await patch.mutateAsync({ serviceModuleId, id: entry.id, data: { approval: status } })
      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompanyQueryKey(serviceModuleId),
      })
      notify.success(t('timetracking.statusToast', { status: getTimeTrackingStatusLabel(t, status) }))
    } catch (error) {
      notify.apiError(error, t('timetracking.updateError'))
    } finally {
      setUpdatingId(null)
    }
  }

  return {
    rows: list?.items,
    totalCount: list?.totalCount,
    isLoading: listQuery.isLoading || listQuery.isFetching,
    isError: listQuery.isError,
    sort,
    order,
    onSort: (newSort?: string, newOrder?: OrderType) => {
      setSort(newSort)
      setOrder(newOrder)
      setOffset(0)
    },
    offset,
    limit,
    onOffsetChange: setOffset,
    onPageSizeChange: (pageSize: number) => {
      setLimit(pageSize)
      setOffset(0)
    },
    updatingId,
    updateStatus,
  }
}

export type TimeTrackingEntriesController = ReturnType<typeof useTimeTrackingEntries>
